import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  notification,
  Row,
  Col,
  Tooltip,
} from "antd";
import ProjectSettingToggle from "../ProjectSettingToggle";
import dayjs from "dayjs";
import { toUTCMidnight } from "../../utils/dateUtils";
import { DATE_PICKER_FORMAT } from "../../constants/dateConstants";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchProjectUsers } from "../../services/projectsAPI";
import {
  createSubmittal,
  fetchNextSubmittalNumber,
  createSubmittalCoversheet,
} from "../../services/submittalsAPI";
import { useAuth } from "../../hooks/useAuth";
import { filterOption } from "../../utils/textUtils";
import {
  CloseOutlined,
  PlusOutlined,
  EditOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import AttachmentsSection from "../AttachmentsSection";
import { fetchVendors } from "../../services/vendorsAPI";
import {
  fetchTemplates,
  createTemplate,
  updateTemplate,
} from "../../services/templatesAPI";
import TemplateModal from "../TemplateModal";
import ProjectUserSelect from "../ProjectUserSelect";
import SpecSectionSelect from "../SpecSectionSelect";
import { normalizeBooleanValue } from "../../utils/projectUtils";
import {
  COVERSHEET_REQUIRES_PDF_TOOLTIP,
  hasPdfAttachments,
} from "../../utils/attachmentUtils";

const { TextArea } = Input;

const richTextToPlainText = (html) => {
  if (!html) return "";
  const withLineBreaks = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li)>\s*<(p|div|li)\b[^>]*>/gi, "\n")
    .replace(/<\/?(p|div|li)\b[^>]*>/gi, "\n");
  const doc = new DOMParser().parseFromString(withLineBreaks, "text/html");
  return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
};

const CreateShopDrawingModal = ({ visible, onClose }) => {
  const { selectedProject, currentUser } = useAuth();
  const projectId = selectedProject?.project_id;
  const trackMaterialRelease = normalizeBooleanValue(
    selectedProject?.track_material_release
  );
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [generateCoversheet, setGenerateCoversheet] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [ccSearchValue, setCcSearchValue] = useState("");
  const attachmentsValue = Form.useWatch("attachments", form);
  const coversheetEnabled = hasPdfAttachments(attachmentsValue);

  const { data: users } = useQuery({
    queryKey: ["projectUsers", projectId, 1, 1000],
    queryFn: () => fetchProjectUsers(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const { data: vendors } = useQuery({
    queryKey: ["vendors", projectId, 1, 1000],
    queryFn: () => fetchVendors(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ["templates", projectId, "submittal"],
    queryFn: () => fetchTemplates(projectId, "submittal"),
    enabled: !!projectId,
  });

  const mutation = useMutation({
    mutationFn: ({ submittalData, coversheetId, sendEmail }) =>
      createSubmittal(submittalData, coversheetId, sendEmail),
    onSuccess: () => {
      queryClient.invalidateQueries(["submittals", projectId]);
      onClose();
      form.resetFields();
      setGenerateCoversheet(false);
      notification.success({ message: "Shop Drawing created successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to create shop drawing",
        description: error.response?.data?.detail?.message || "Unknown error",
      });
    },
  });

  const coversheetMutation = useMutation({
    mutationFn: createSubmittalCoversheet,
    onError: (error) => {
      notification.error({
        message: "Failed to create coversheet",
        description: error.response?.data?.detail?.message || "Unknown error",
      });
    },
  });

  useEffect(() => {
    if (visible && projectId) {
      fetchNextSubmittalNumber(projectId, null).then((data) => {
        form.setFieldsValue({
          number: data.next_number,
          revision: 0,
          submittal_manager_id: currentUser.id,
          issue_date: dayjs(),
        });
      });
    }
  }, [visible]);

  useEffect(() => {
    if (!coversheetEnabled && generateCoversheet) {
      setGenerateCoversheet(false);
      form.setFieldsValue({ generate_coversheet: false });
    }
  }, [coversheetEnabled, generateCoversheet, form]);

  const handleSpecSectionChange = (value) => {
    fetchNextSubmittalNumber(projectId, value).then((data) => {
      form.setFieldsValue({ number: data.next_number });
    });
  };

  const handleReceivedFromChange = (value) => {
    if (value) {
      const receivedFrom = users.data.find(
        (user) => user.id === value || user._id === value
      );
      if (receivedFrom?.vendor_id) {
        form.setFieldsValue({
          responsible_contractor_id: receivedFrom.vendor_id,
        });
      }
    } else {
      // Clear the responsible contractor when received from is cleared
      form.setFieldsValue({
        responsible_contractor_id: undefined,
      });
    }
  };

  const handleCreateSubmittal = async (values) => {
    const submittalData = {
      ...values,
      project_id: projectId,
      type: "Shop Drawing",
      due_date: values.due_date ? toUTCMidnight(values.due_date) : null,
      issue_date: values.issue_date ? toUTCMidnight(values.issue_date) : null,
      released_on: values.released_on ? toUTCMidnight(values.released_on) : null,
      estimated_delivery_date: values.estimated_delivery_date
        ? toUTCMidnight(values.estimated_delivery_date)
        : null,
      steps: values.steps
        ? values.steps.map((step) => ({
            reviews: step.reviews.map((reviewer) => ({
              reviewer_id: reviewer,
            })),
          }))
        : [],
      attachments: values.attachments?.map((file) => file) || [],
    };

    try {
      if (generateCoversheet) {
        const coversheetResponse = await coversheetMutation.mutateAsync(
          submittalData
        );
        mutation.mutate({
          submittalData,
          coversheetId: coversheetResponse.coversheet_id,
          sendEmail: form.getFieldValue("email_notifications") ?? true,
        });
      } else {
        mutation.mutate({
          submittalData,
          coversheetId: null,
          sendEmail: form.getFieldValue("email_notifications") ?? true,
        });
      }
    } catch (error) {
      // Error handling is done in the mutation callbacks
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedTemplate(null);
    onClose();
  };

  const handleTemplateChange = async (templateId) => {
    if (templateId) {
      const template = templates?.templates?.find((t) => t._id === templateId);
      setSelectedTemplate(template);

      // Check if form has existing data
      const formValues = form.getFieldsValue();
      const hasExistingData = !!(
        formValues.title ||
        formValues.description ||
        formValues.due_date ||
        formValues.issue_date ||
        formValues.received_from_id ||
        formValues.responsible_contractor_id ||
        formValues.specification_section_id ||
        formValues.submittal_manager_id ||
        (formValues.distribution_list &&
          formValues.distribution_list.length > 0) ||
        (formValues.attachments && formValues.attachments.length > 0)
      );

      const populateTemplateData = async () => {
        // Fetch next shop drawing number for the template's spec section
        let nextNumber = null;
        if (template.specification_section_id) {
          try {
            const numberData = await fetchNextSubmittalNumber(
              projectId,
              template.specification_section_id
            );
            nextNumber = numberData.next_number;
          } catch (error) {
            console.error("Error fetching next shop drawing number:", error);
            // Fallback to current number if there's an error
            nextNumber = formValues.number;
          }
        }

        // Populate form with template data
        form.setFieldsValue({
          template_id: templateId, // Set the template_id to show selected template in dropdown
          description: richTextToPlainText(template.description),
          priority: template.priority,
          issue_date: dayjs(),
          due_date: dayjs().add(template.days_duration, "day"),
          received_from_id: template.received_from_id,
          responsible_contractor_id: template.responsible_contractor_id,
          specification_section_id: template.specification_section_id,
          submittal_manager_id: template.submittal_manager_id,
          reviewer_ids: template.reviewer_ids,
          distribution_list: template.distribution_list,
          steps: template.steps
            ? template.steps.map((step) => ({
                reviews: step.reviews.map((review) => review.reviewer_id),
              }))
            : [],
          ...(nextNumber && { number: nextNumber }), // Update number if we got a new one
        });
      };

      if (hasExistingData) {
        // Show confirmation modal
        Modal.confirm({
          title: "Are you sure?",
          content:
            "There is existing data in the form. Are you sure you want to replace it with template data?",
          okText: "Yes",
          cancelText: "No",
          onOk: populateTemplateData,
          onCancel: () => {
            // Reset template selection if user cancels
            form.setFieldsValue({ template_id: null });
            setSelectedTemplate(null);
          },
        });
      } else {
        // No existing data, populate directly
        await populateTemplateData();
      }
    } else {
      setSelectedTemplate(null);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setIsTemplateModalVisible(true);
  };

  const handleEditTemplate = () => {
    if (selectedTemplate) {
      setEditingTemplate(selectedTemplate);
      setIsTemplateModalVisible(true);
    }
  };

  const handleTemplateModalClose = () => {
    setIsTemplateModalVisible(false);
    setEditingTemplate(null);
  };

  const handleTemplateDeleted = () => {
    setSelectedTemplate(null);
  };

  const handleTemplateSave = async (templateData) => {
    try {
      const processedTemplateData = {
        ...templateData,
        steps: templateData.steps
          ? templateData.steps.map((step) => ({
              reviews: step.reviews.map((reviewerId) => ({
                reviewer_id: reviewerId,
              })),
            }))
          : [],
      };

      if (editingTemplate) {
        const updatedTemplate = await updateTemplate(
          editingTemplate._id,
          processedTemplateData
        );
        notification.success({ message: "Template updated successfully" });

        // Update selectedTemplate with the updated data
        setSelectedTemplate(updatedTemplate);

        // Fetch next shop drawing number for the updated template's spec section
        let nextNumber = null;
        if (updatedTemplate.specification_section_id) {
          try {
            const numberData = await fetchNextSubmittalNumber(
              projectId,
              updatedTemplate.specification_section_id
            );
            nextNumber = numberData.next_number;
          } catch (error) {
            console.error("Error fetching next shop drawing number:", error);
            // Keep current number if there's an error
            nextNumber = form.getFieldValue("number");
          }
        }

        // Update form with updated template data
        form.setFieldsValue({
          template_id: updatedTemplate._id,
          description: richTextToPlainText(updatedTemplate.description),
          priority: updatedTemplate.priority,
          issue_date: dayjs(),
          due_date: dayjs().add(updatedTemplate.days_duration, "day"),
          received_from_id: updatedTemplate.received_from_id,
          responsible_contractor_id: updatedTemplate.responsible_contractor_id,
          specification_section_id: updatedTemplate.specification_section_id,
          submittal_manager_id: updatedTemplate.submittal_manager_id,
          reviewer_ids: updatedTemplate.reviewer_ids,
          distribution_list: updatedTemplate.distribution_list,
          steps: updatedTemplate.steps
            ? updatedTemplate.steps.map((step) => ({
                reviews: step.reviews.map((review) => review.reviewer_id),
              }))
            : [],
          ...(nextNumber && { number: nextNumber }), // Update number if we got a new one
        });
      } else {
        await createTemplate({
          ...processedTemplateData,
          project_id: projectId,
          type: "submittal",
        });
        notification.success({ message: "Template created successfully" });
      }
      queryClient.invalidateQueries(["templates", projectId, "submittal"]);
      handleTemplateModalClose();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.detail?.message ||
        error?.message ||
        "Failed to save template";
      notification.error({
        message: "Failed to save template",
        description: errorMessage,
      });
    }
  };

  return (
    <Modal
      title="Create Shop Drawing"
      open={visible}
      onCancel={handleClose}
      footer={null}
      className="w-full max-w-4xl"
    >
      {/* Template Selection - Ultra Compact */}
      <div className="mb-3 pb-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
            <FileTextOutlined className="text-xs" />
            Template:
          </span>
          <Select
            placeholder="Select template..."
            value={selectedTemplate?._id}
            onChange={handleTemplateChange}
            allowClear
            loading={templatesLoading}
            options={templates?.templates?.map((template) => ({
              value: template._id,
              label: template.name,
            }))}
            className="flex-1"
            size="small"
          />
          <Button
            type="text"
            icon={<PlusOutlined />}
            onClick={handleCreateTemplate}
            size="small"
            className="p-1 h-6 w-6 text-gray-500 hover:text-blue-500"
            title="Add Template"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={handleEditTemplate}
            disabled={!selectedTemplate}
            size="small"
            className="p-1 h-6 w-6 text-gray-500 hover:text-blue-500"
            title="Edit Template"
          />
        </div>
      </div>

      <Form form={form} onFinish={handleCreateSubmittal} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Number"
              name="number"
              rules={[
                {
                  required: true,
                  message: "Please input the submittal number!",
                },
              ]}
              className="w-full"
            >
              <Input type="number" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Revision"
              name="revision"
              rules={[
                { required: true, message: "Please input the revision!" },
              ]}
              initialValue={0}
              className="w-full"
            >
              <Input type="number" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Please input the title!" }]}
          className="w-full"
        >
          <Input />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Specification Section"
              name="specification_section_id"
              className="w-full"
              getValueFromEvent={(value) => {
                handleSpecSectionChange(value);
                return value;
              }}
            >
              <SpecSectionSelect className="w-full" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Shop Drawing Manager"
              name="submittal_manager_id"
              rules={[
                {
                  required: true,
                  message: "Please select the submittal manager!",
                },
              ]}
              className="w-full"
            >
              <ProjectUserSelect
                mode={null}
                placeholder="Select shop drawing manager"
                className="w-full"
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Description" name="description" className="w-full">
          <TextArea />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="EDMS" name="edms" className="w-full">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Priority"
              name="priority"
              className="w-full"
              initialValue="Medium"
            >
              <Select
                options={[
                  { value: "High", label: "High" },
                  { value: "Medium", label: "Medium" },
                  { value: "Low", label: "Low" },
                ]}
                className="w-full"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Issue Date" name="issue_date" className="w-full">
              <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Due Date" name="due_date" className="w-full">
              <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
            </Form.Item>
          </Col>
        </Row>
        {trackMaterialRelease && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Released On" name="released_on">
                <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Estimated Delivery Date"
                name="estimated_delivery_date"
              >
                <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
              </Form.Item>
            </Col>
          </Row>
        )}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Received From"
              name="received_from_id"
              className="w-full"
              getValueFromEvent={(value) => {
                handleReceivedFromChange(value);
                return value;
              }}
            >
              <ProjectUserSelect
                mode={null}
                allowClear
                placeholder="Select received from"
                className="w-full"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Responsible Contractor"
              name="responsible_contractor_id"
              className="w-full"
            >
              <Select
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                options={vendors?.vendors?.map((vendor) => ({
                  value: vendor.id,
                  label: vendor.name,
                }))}
                className="w-full"
                disabled
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Cc" name="distribution_list" className="w-full">
          <ProjectUserSelect
            searchValue={ccSearchValue}
            onSearch={setCcSearchValue}
            placeholder="Select Cc"
            className="w-full"
          />
        </Form.Item>
        <Form.Item name="attachments" label="Attachments" className="w-full">
          <AttachmentsSection projectId={projectId} />
        </Form.Item>
        <Form.List name="steps">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, fieldKey }, index) => (
                <div key={key} className="mb-4 flex items-center">
                    <Form.Item
                      label={`Step #${index + 1}`}
                      name={[name, "reviews"]}
                      fieldKey={[fieldKey, "reviews"]}
                      rules={[
                        {
                          required: true,
                          message: "Please select at least one reviewer",
                        },
                      ]}
                      className="w-full"
                    >
                      <ProjectUserSelect
                        placeholder="Select reviewers"
                        className="flex-grow"
                      />
                    </Form.Item>
                  <Button
                    type="danger"
                    onClick={() => remove(name)}
                    icon={<CloseOutlined />}
                    className="ml-2"
                  />
                </div>
              ))}
              <Button
                type="dashed"
                onClick={() => add()}
                block
                className="mb-4"
              >
                Add Reviewer
              </Button>
            </>
          )}
        </Form.List>
        <div className="project-settings-grid mt-6">
            <Tooltip
              title={
                !coversheetEnabled ? COVERSHEET_REQUIRES_PDF_TOOLTIP : null
              }
            >
              <ProjectSettingToggle name="generate_coversheet" title="Generate Coversheet" disabled={!coversheetEnabled} onChange={setGenerateCoversheet} />
            </Tooltip>
            <ProjectSettingToggle name="email_notifications" title="Email Notifications" initialValue={true} />
        </div>
        <Form.Item className="w-full">
          <Button
            type="primary"
            onClick={() => form.submit()} // Just submit without changing sendEmail
            loading={mutation.isPending || coversheetMutation.isPending}
            className="w-full mt-4"
          >
            {form.getFieldValue("email_notifications") ?? true
              ? "Create and Send Emails"
              : "Create Shop Drawing"}
          </Button>
        </Form.Item>
      </Form>

      {/* Template Modal */}
      <TemplateModal
        visible={isTemplateModalVisible}
        onClose={handleTemplateModalClose}
        onSave={handleTemplateSave}
        editingTemplate={editingTemplate}
        templateType="submittal"
        onTemplateDeleted={handleTemplateDeleted}
      />
    </Modal>
  );
};

export default CreateShopDrawingModal;
